import type {
    ConversionRatesOvertimeListData,
    ConversionRatesOvertimeListParams,
    ConversionRatesOvertimePopListData,
    ConversionRatesOvertimePopListParams,
} from "@similarweb/pro-api-client";
import { SOURCES_TEXT } from "app/components/widget/widget-types/WidgetConstants";
import { getGetExcel } from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import DurationServiceSingleton from "app/services/DurationService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import type { ConversionMetric } from "./constants";
import { CONVERSIONS_EXCEL_ENDPOINT } from "./constants";
import type { ConversionRatesParams, MetricSiteData, WebSource } from "./types";

const WEB_SOURCES = new Set<string>(Object.keys(SOURCES_TEXT.websites));

const isWebSource = (raw: string): raw is WebSource => WEB_SOURCES.has(raw);

/** Route params carry the web source as a free-form string; anything unrecognised falls back to Total. */
export const toWebSource = (raw: string | undefined): WebSource =>
    raw !== undefined && isWebSource(raw) ? raw : devicesTypes.TOTAL;

const createBaseParams = (params: ConversionRatesParams) => {
    const { duration, comparedDuration, key, isWWW } = params;
    const { from, to, compareFrom, compareTo } = DurationServiceSingleton.getDurationData(
        duration,
        comparedDuration,
    ).forAPI;

    const country = Number(params.country);

    return {
        country: Number.isFinite(country) ? country : 999,
        keys: (key ?? "").split(",").filter(Boolean),
        from,
        to,
        compareFrom,
        compareTo,
        includeSubdomains: isWWW === "*",
    } satisfies ConversionRatesOvertimePopListParams;
};

export const createParams = (params: ConversionRatesParams): ConversionRatesOvertimeListParams => {
    const { compareFrom, compareTo, ...singleParams } = createBaseParams(params);

    return singleParams;
};

export const createPopParams = (params: ConversionRatesParams): ConversionRatesOvertimePopListParams => {
    const base = createBaseParams(params);

    return { ...base, compareFrom: base.compareFrom ?? "", compareTo: base.compareTo ?? "" };
};

export const getExcelUrl = (params: ConversionRatesOvertimeListParams): string =>
    `/${getGetExcel(CONVERSIONS_EXCEL_ENDPOINT)(params)}`;

/** Picks one metric's series out of the combined Overtime response so the chart code sees `{ site, data, average }`. */
export const selectMetric = (
    rawData: ConversionRatesOvertimeListData | ConversionRatesOvertimePopListData,
    metric: ConversionMetric,
): MetricSiteData[] => rawData.map((item) => ({ site: item.site, ...item[metric] }));
